import React from 'react';

import {
  HStack,
  Icon,
  NewButton,
  Text,
  Textarea,
  VStack,
} from '@eluve/components';
import { FeedbackTypeEnum } from '@eluve/graphql-types';

export interface InlineFeedbackProps {
  sectionLabel: string;
  feedbackType: FeedbackTypeEnum | null;
  isSubmitting: boolean;
  isFinalized: boolean;
  onThumbsClick: (type: FeedbackTypeEnum) => void;
  onCommentSubmit: (comment: string) => void;
  onClose?: () => void;
  justify?: 'start' | 'center' | 'end';
}

export const InlineFeedback: React.FC<InlineFeedbackProps> = ({
  sectionLabel,
  feedbackType,
  isSubmitting,
  isFinalized,
  onThumbsClick,
  onCommentSubmit,
  onClose,
  justify = 'end',
}) => {
  const [comment, setComment] = React.useState<string>('');
  const disableAll = isSubmitting || isFinalized;

  const handleThumbs = (type: FeedbackTypeEnum) => {
    if (disableAll) {
      return;
    }
    onThumbsClick(type);
  };

  const handleSubmitClick = () => {
    if (!feedbackType || isFinalized) {
      return;
    }
    onCommentSubmit(comment);
  };

  const handleCloseClick = () => {
    if (isFinalized) {
      return;
    }
    onClose && onClose();
    setComment('');
  };

  return (
    <VStack wFull align="stretch">
      <HStack wFull justify={justify} align="center">
        <Text color="secondary" size="md" weight="regular">
          {sectionLabel}
        </Text>
        <NewButton
          text="Yes"
          size="s"
          type={feedbackType === 'POSITIVE' ? 'primary' : 'outlineSubtle'}
          disabled={disableAll}
          icon={{ name: 'ThumbsUp' }}
          onClick={() => handleThumbs('POSITIVE')}
        />
        <NewButton
          text="No"
          size="s"
          type={feedbackType === 'NEGATIVE' ? 'primary' : 'outlineSubtle'}
          disabled={disableAll}
          icon={{ name: 'ThumbsDown' }}
          onClick={() => handleThumbs('NEGATIVE')}
        />
      </HStack>

      {feedbackType && !isFinalized && (
        <div className="relative rounded-md bg-bg-secondary">
          {onClose && (
            <HStack justify="end">
              <button
                className="m-3 cursor-pointer"
                onClick={handleCloseClick}
                aria-label="Close"
              >
                <Icon name="X" size="xs" />
              </button>
            </HStack>
          )}
          <VStack className="px-3" gap={3}>
            <Text weight="bold" size="md" color="tertiary">
              COMMENTS (OPTIONAL)
            </Text>
            <Textarea
              className="w-full resize-y rounded-md text-text-primary outline-none focus-visible:ring-0"
              value={comment}
              placeholder="Tell us more!"
              onChange={(e) => setComment(e.currentTarget.value)}
            />
            <HStack justify="end" className="p-3">
              <NewButton
                text="Submit"
                size="s"
                type="primary"
                disabled={disableAll}
                onClick={handleSubmitClick}
              />
            </HStack>
          </VStack>
        </div>
      )}
    </VStack>
  );
};
